import { useEffect, useState, useCallback } from 'react';
import { Trash2, Loader2, RefreshCw, Film, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 60;

const fullUrl = (u) => (u && u.startsWith('http') ? u : `${BACKEND_URL}${u}`);

// Shared grid of files in the upload folder.
// - onSelect(url): if provided, clicking a tile selects it (picker mode).
// - selectedUrl: highlights the currently selected tile.
// - Delete is usage-checked server-side; a 409 lists blocking content.
export function UploadFolderGrid({ onSelect, selectedUrl }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [blocked, setBlocked] = useState(null); // { filename, usages }

  const load = useCallback(async (offset = 0, append = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/uploads-list?limit=${PAGE_SIZE}&offset=${offset}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Liste alınamadı');
      const data = await res.json();
      setTotal(data.total);
      setItems((prev) => (append ? [...prev, ...data.items] : data.items));
    } catch (err) {
      toast.error(err.message || 'Yükleme klasörü okunamadı');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(0); }, [load]);

  const handleDelete = async (filename) => {
    if (!window.confirm('Bu dosya diskten kalıcı olarak silinecek. Devam edilsin mi?')) return;
    setDeleting(filename);
    setBlocked(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/uploads/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        const d = data.detail || {};
        setBlocked({ filename, message: d.message, usages: d.usages || [] });
        toast.error('Dosya kullanımda, silinemez');
        return;
      }
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(typeof e.detail === 'string' ? e.detail : 'Silme başarısız');
      }
      setItems((prev) => prev.filter((it) => it.filename !== filename));
      setTotal((t) => t - 1);
      toast.success('Dosya silindi');
    } catch (err) {
      toast.error(err.message || 'Silme hatası');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500">{total} dosya</p>
        <button
          type="button"
          onClick={() => load(0)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Yenile
        </button>
      </div>

      {blocked && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <p className="font-medium mb-1">{blocked.message || 'Dosya kullanımda.'}</p>
          <p className="text-xs mb-1">Bu dosyayı kullanan içerikler (önce bunları silin/değiştirin):</p>
          <ul className="list-disc list-inside text-xs space-y-0.5">
            {blocked.usages.map((u, i) => (
              <li key={i}>{u.collection}: {u.title}</li>
            ))}
          </ul>
        </div>
      )}

      {items.length === 0 && !loading ? (
        <p className="text-sm text-gray-400 py-6 text-center">Yükleme klasörü boş.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {items.map((it) => {
            const isSelected = selectedUrl && (selectedUrl === it.url || selectedUrl === fullUrl(it.url));
            return (
              <div
                key={it.filename}
                className={`relative group rounded-lg overflow-hidden border-2 ${isSelected ? 'border-amber-500 ring-2 ring-amber-300' : 'border-gray-200'} bg-gray-50 aspect-square`}
              >
                {onSelect ? (
                  <button
                    type="button"
                    onClick={() => onSelect(fullUrl(it.url))}
                    className="block w-full h-full"
                    title="Bu görseli seç"
                  >
                    <TilePreview it={it} />
                  </button>
                ) : (
                  <TilePreview it={it} />
                )}

                {isSelected && (
                  <div className="absolute top-1 left-1 bg-amber-500 text-white rounded-full p-0.5">
                    <Check className="w-3 h-3" />
                  </div>
                )}

                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDelete(it.filename); }}
                  disabled={deleting === it.filename}
                  className="absolute top-1 right-1 bg-red-600 text-white rounded-md p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 disabled:opacity-100"
                  title="Diskten sil"
                >
                  {deleting === it.filename ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {items.length < total && (
        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={() => load(items.length, true)}
            disabled={loading}
            className="text-sm px-4 py-2 rounded-md border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-60"
          >
            {loading ? 'Yükleniyor...' : 'Daha fazla yükle'}
          </button>
        </div>
      )}
    </div>
  );
}

function TilePreview({ it }) {
  if (it.kind === 'video') {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 bg-gray-100">
        <Film className="w-6 h-6" />
        <span className="text-[9px] px-1 truncate max-w-full mt-1">{it.filename}</span>
      </div>
    );
  }
  return <img src={fullUrl(it.url)} alt={it.filename} className="w-full h-full object-cover" loading="lazy" />;
}
